/**
 * Webtech HR Engine™ - SuperAdmin Modules Dashboard
 * Phase 5: Gestion des Modules & Feature Flags
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Skeleton } from './ui/skeleton';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

// Icons
import {
  Users,
  Image as ImageIcon,
  Network,
  Target,
  Award,
  Brain,
  BarChart,
  Star,
  Lightbulb,
  Search,
  RefreshCw,
  Filter,
  Settings,
  ToggleLeft,
  ToggleRight,
  ChevronRight,
  Building2,
  Crown,
  Eye,
  MoreVertical,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Package,
  Layers,
  Zap,
  Lock,
  Unlock,
  History,
  Undo2,
} from 'lucide-react';

import SuperAdminWipModulesPanel from './SuperAdminWipModulesPanel';
import SuperAdminMaintenancePanel from './SuperAdminMaintenancePanel';

// Icon mapping for modules
const MODULE_ICONS = {
  peopleMapping: Users,
  trombinoscope: ImageIcon,
  organigramme: Network,
  performance: Target,
  skills: Award,
  aiAnalysis: Brain,
  advancedReporting: BarChart,
  scoring: Star,
  recommendations: Lightbulb,
};

// Category colors
const CATEGORY_COLORS = {
  core: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  advanced: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  premium: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
};

const CATEGORY_LABELS = {
  core: 'Core',
  advanced: 'Avancé',
  premium: 'Premium',
};

// Module Card Component
function ModuleCard({ module, onToggleGlobal, onViewDetails }) {
  const IconComponent = MODULE_ICONS[module.id] || Package;
  const categoryStyle = CATEGORY_COLORS[module.category] || CATEGORY_COLORS.core;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${categoryStyle.bg}`}>
              <IconComponent className={`w-5 h-5 ${categoryStyle.text}`} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{module.name}</h3>
              <Badge className={`${categoryStyle.bg} ${categoryStyle.text} mt-1`}>
                {CATEGORY_LABELS[module.category]}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onViewDetails(module)}>
            <Eye className="w-4 h-4 text-slate-500" />
          </Button>
        </div>

        <p className="text-sm text-slate-600 mb-4">{module.description}</p>

        {/* Stats by Plan */}
        <div className="space-y-3">
          {/* FREE Plan */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">STARTER</span>
              <span className="font-medium">
                {module.stats?.free?.enabled || 0}/{module.stats?.free?.total || 0}
                <span className="text-slate-400 ml-1">
                  ({module.stats?.free?.percentage || 0}%)
                </span>
              </span>
            </div>
            <Progress value={module.stats?.free?.percentage || 0} className="h-1.5" />
          </div>

          {/* PRO Plan */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-amber-600">
                <Crown className="w-3 h-3" />
                PRO
              </span>
              <span className="font-medium">
                {module.stats?.pro?.enabled || 0}/{module.stats?.pro?.total || 0}
                <span className="text-slate-400 ml-1">
                  ({module.stats?.pro?.percentage || 0}%)
                </span>
              </span>
            </div>
            <Progress value={module.stats?.pro?.percentage || 0} className="h-1.5 bg-amber-100" />
          </div>
        </div>

        {/* Toggle Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onToggleGlobal(module, 'free')}
                >
                  <ToggleRight className="w-3 h-3 mr-1" />
                  FREE
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Toggle pour tous les comptes FREE
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onToggleGlobal(module, 'pro')}
                >
                  <Crown className="w-3 h-3 mr-1" />
                  PRO
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Toggle pour tous les comptes PRO
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}

// Company Row Component
function CompanyRow({ company, modules, onToggleModule, onViewCompany }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow className="hover:bg-slate-50">
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-webtech-blue to-blue-700 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              {company.companyName?.charAt(0) || 'E'}
            </div>
            <div>
              <p className="font-medium text-slate-900">{company.companyName}</p>
              <p className="text-xs text-slate-500">
                {company.activeModulesCount}/{company.totalModules} modules actifs
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge className={company.plan === 'pro' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
            {company.plan === 'pro' ? (
              <>
                <Crown className="w-3 h-3 mr-1" />
                PRO
              </>
            ) : (
              'STARTER'
            )}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
            {company.status === 'active' ? 'Actif' : company.status}
          </Badge>
        </TableCell>
        <TableCell>
          <Progress 
            value={(company.activeModulesCount / company.totalModules) * 100} 
            className="w-24 h-2"
          />
        </TableCell>
        <TableCell>
          {Object.keys(company.moduleOverrides || {}).length > 0 && (
            <Badge variant="outline" className="text-xs">
              <Settings className="w-3 h-3 mr-1" />
              {Object.keys(company.moduleOverrides).length} override(s)
            </Badge>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onViewCompany(company)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Voir les détails
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setExpanded(!expanded)}>
                  <Layers className="w-4 h-4 mr-2" />
                  {expanded ? 'Masquer' : 'Afficher'} modules
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded Row - Module Toggles */}
      {expanded && (
        <TableRow className="bg-slate-50/50">
          <TableCell colSpan={6} className="p-4">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(company.activeModules || {}).map(([moduleId, enabled]) => {
                const moduleDef = modules.find(m => m.id === moduleId);
                const IconComponent = MODULE_ICONS[moduleId] || Package;
                const isOverridden = company.moduleOverrides?.[moduleId]?.overridden;

                return (
                  <div
                    key={moduleId}
                    className={`p-3 rounded-lg border ${
                      enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                    } ${isOverridden ? 'ring-2 ring-purple-300' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <IconComponent className={`w-4 h-4 ${enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                      {isOverridden && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Settings className="w-3 h-3 text-purple-500" />
                            </TooltipTrigger>
                            <TooltipContent>Exception personnalisée</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="text-xs font-medium text-slate-700 mb-2 truncate">
                      {moduleDef?.name || moduleId}
                    </p>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) => onToggleModule(company, moduleId, checked)}
                      className="scale-75"
                    />
                  </div>
                );
              })}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Global Toggle Dialog
function GlobalToggleDialog({ isOpen, onClose, module, plan, onConfirm }) {
  const [enabled, setEnabled] = useState(true);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && module) {
      // Set initial enabled based on current percentage
      const stats = plan === 'pro' ? module.stats?.pro : module.stats?.free;
      setEnabled((stats?.percentage || 0) < 50);
      setReason('');
    }
  }, [isOpen, module, plan]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onConfirm(module.id, plan, enabled, reason);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!module) return null;

  const IconComponent = MODULE_ICONS[module.id] || Package;
  const stats = plan === 'pro' ? module.stats?.pro : module.stats?.free;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${plan === 'pro' ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <IconComponent className={`w-5 h-5 ${plan === 'pro' ? 'text-amber-700' : 'text-blue-700'}`} />
            </div>
            Toggle Global - {module.name}
          </DialogTitle>
          <DialogDescription>
            Modifier le statut du module pour tous les comptes {plan?.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Warning */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Attention</p>
              <p>Cette action affectera <strong>{stats?.total || 0}</strong> entreprise(s) {plan?.toUpperCase()}.</p>
            </div>
          </div>

          {/* Current State */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600 mb-1">État actuel:</p>
            <p className="font-medium">
              {stats?.enabled || 0} / {stats?.total || 0} entreprises ont ce module activé ({stats?.percentage || 0}%)
            </p>
          </div>

          {/* Toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <Label htmlFor="toggle-enabled" className="font-medium">
              {enabled ? 'Activer' : 'Désactiver'} pour tous
            </Label>
            <Switch
              id="toggle-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Raison (optionnel)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motif de ce changement..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className={enabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {enabled ? 'Activer' : 'Désactiver'} pour tous
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Module Details Dialog
function ModuleDetailsDialog({ isOpen, onClose, moduleId, onRemoveOverride }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/superadmin/modules/${moduleId}/details`);
      setDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (isOpen && moduleId) {
      fetchDetails();
    }
  }, [isOpen, moduleId, fetchDetails]);

  if (!details?.module) return null;

  const IconComponent = MODULE_ICONS[moduleId] || Package;
  const categoryStyle = CATEGORY_COLORS[details.module.category] || CATEGORY_COLORS.core;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${categoryStyle.bg}`}>
              <IconComponent className={`w-6 h-6 ${categoryStyle.text}`} />
            </div>
            <div>
              <span>{details.module.name}</span>
              <Badge className={`${categoryStyle.bg} ${categoryStyle.text} ml-2`}>
                {CATEGORY_LABELS[details.module.category]}
              </Badge>
            </div>
          </DialogTitle>
          <DialogDescription>{details.module.description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{details.stats?.totalEnabled || 0}</p>
                <p className="text-xs text-slate-500">Total activés</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-2xl font-bold text-blue-700">{details.stats?.byPlan?.free || 0}</p>
                <p className="text-xs text-slate-500">STARTER</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <p className="text-2xl font-bold text-amber-700">{details.stats?.byPlan?.pro || 0}</p>
                <p className="text-xs text-slate-500">BUSINESS</p>
              </div>
            </div>

            {/* Overrides */}
            {details.overrides?.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-purple-500" />
                  Exceptions personnalisées ({details.overrides.length})
                </h4>
                <div className="space-y-2">
                  {details.overrides.map((override) => (
                    <div
                      key={override.companyId}
                      className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center text-purple-700 font-bold text-sm">
                          {override.companyName?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{override.companyName}</p>
                          <p className="text-xs text-slate-500">
                            {override.enabled ? 'Activé' : 'Désactivé'} par {override.overriddenBy}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={override.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {override.enabled ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                          {override.enabled ? 'ON' : 'OFF'}
                        </Badge>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => onRemoveOverride(override.companyId, moduleId)}
                              >
                                <Undo2 className="w-4 h-4 text-slate-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Supprimer exception</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Global Configs */}
            {details.globalConfigs?.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Historique des modifications globales
                </h4>
                <div className="space-y-2">
                  {details.globalConfigs.map((config, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm"
                    >
                      <div>
                        <span className="font-medium">{config.plan?.toUpperCase()}</span>
                        <span className="mx-2">→</span>
                        <Badge className={config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {config.enabled ? 'Activé' : 'Désactivé'}
                        </Badge>
                      </div>
                      <span className="text-slate-500">
                        {config.updatedBy} - {new Date(config.updatedAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function SuperAdminModulesDashboard() {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialogs
  const [globalToggleDialog, setGlobalToggleDialog] = useState({ open: false, module: null, plan: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, moduleId: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [modulesRes, companiesRes] = await Promise.all([
        api.get('/superadmin/modules'),
        api.get('/superadmin/modules/companies')
      ]);
      
      setModules(modulesRes.data.modules || []);
      setStats(modulesRes.data.stats);
      setCompanies(companiesRes.data.companies || []);
    } catch (error) {
      console.error('Error fetching modules data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle module globally
  const handleGlobalToggle = async (moduleId, plan, enabled, reason) => {
    try {
      await api.put(`/superadmin/modules/${moduleId}/toggle-global`, {
        plan,
        enabled,
        reason
      });
      toast.success(`Module ${enabled ? 'activé' : 'désactivé'} pour tous les comptes ${plan?.toUpperCase()}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
      throw error;
    }
  };

  // Toggle module for specific company
  const handleCompanyModuleToggle = async (company, moduleId, enabled) => {
    try {
      await api.put(`/superadmin/companies/${company.companyId}/modules/${moduleId}/toggle`, {
        enabled,
        reason: 'Modification manuelle SuperAdmin'
      });
      toast.success(`Module ${enabled ? 'activé' : 'désactivé'} pour ${company.companyName}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  // Remove module override
  const handleRemoveOverride = async (companyId, moduleId) => {
    try {
      await api.delete(`/superadmin/companies/${companyId}/modules/${moduleId}/override`);
      toast.success('Exception supprimée');
      fetchData();
      setDetailsDialog({ open: false, moduleId: null });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Filter modules by category
  const filteredModules = modules.filter(m => {
    if (categoryFilter === 'all') return true;
    return m.category === categoryFilter;
  });

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'all' || company.plan === planFilter;
    return matchesSearch && matchesPlan;
  });

  return (
    <div className="space-y-6">
      {/* 🚧 Modules en développement (WIP) — section dédiée en haut */}
      <SuperAdminWipModulesPanel />

      {/* 🔧 Maintenance & nettoyage (politique mai 2026) */}
      <SuperAdminMaintenancePanel />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-webtech-blue" />
            Gestion des Modules & Features
          </h2>
          <p className="text-slate-600 mt-1">
            Gérez les fonctionnalités disponibles pour vos clients
          </p>
        </div>
        
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-xl">
                <Package className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.totalModules || 0}</p>
                <p className="text-sm text-slate-500">Modules</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-100 rounded-xl">
                <Zap className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.categories?.core || 0}</p>
                <p className="text-sm text-slate-500">Modules Core</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-100 rounded-xl">
                <Target className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.categories?.advanced || 0}</p>
                <p className="text-sm text-slate-500">Modules Avancés</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-xl">
                <Crown className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.categories?.premium || 0}</p>
                <p className="text-sm text-slate-500">Modules Premium</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="modules" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="modules" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Vue Modules
          </TabsTrigger>
          <TabsTrigger value="companies" className="data-[state=active]:bg-white">
            <Building2 className="w-4 h-4 mr-2" />
            Par Entreprise
          </TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          {/* Category Filter */}
          <div className="flex gap-2">
            <Button
              variant={categoryFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('all')}
            >
              Tous
            </Button>
            <Button
              variant={categoryFilter === 'core' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('core')}
              className={categoryFilter === 'core' ? 'bg-blue-600' : ''}
            >
              Core
            </Button>
            <Button
              variant={categoryFilter === 'advanced' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('advanced')}
              className={categoryFilter === 'advanced' ? 'bg-purple-600' : ''}
            >
              Avancé
            </Button>
            <Button
              variant={categoryFilter === 'premium' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('premium')}
              className={categoryFilter === 'premium' ? 'bg-amber-600' : ''}
            >
              Premium
            </Button>
          </div>

          {/* Modules Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredModules.map(module => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  onToggleGlobal={(m, plan) => setGlobalToggleDialog({ open: true, module: m, plan })}
                  onViewDetails={(m) => setDetailsDialog({ open: true, moduleId: m.id })}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher une entreprise..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les plans</SelectItem>
                    <SelectItem value="free">STARTER</SelectItem>
                    <SelectItem value="pro">BUSINESS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Companies Table */}
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredCompanies.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucune entreprise trouvée</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Modules</TableHead>
                      <TableHead>Exceptions</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.map(company => (
                      <CompanyRow
                        key={company.companyId}
                        company={company}
                        modules={modules}
                        onToggleModule={handleCompanyModuleToggle}
                        onViewCompany={() => {}}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <GlobalToggleDialog
        isOpen={globalToggleDialog.open}
        onClose={() => setGlobalToggleDialog({ open: false, module: null, plan: null })}
        module={globalToggleDialog.module}
        plan={globalToggleDialog.plan}
        onConfirm={handleGlobalToggle}
      />

      <ModuleDetailsDialog
        isOpen={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, moduleId: null })}
        moduleId={detailsDialog.moduleId}
        onRemoveOverride={handleRemoveOverride}
      />
    </div>
  );
}
